/**
 * خدمات التنظيم: الشركات، العلامات، الفروع، الفئات، العضويات، الأدوار والإشراف.
 * كل كتابة تمرّ عبر طبقة البيانات التي تفرض الصلاحيات، وتُسجّل الأحداث الحساسة
 * في سجل التدقيق.
 */

import type { ArchivedOrgRecord, DataGateway, OrgArchiveQuery, OrgLifecycleInput, RoleDeletionImpact } from '@/data/types';
import { AppError } from '@/domain/errors';
import { newId } from '@/domain/ids';
import { AUDIT_ACTIONS } from '@/domain/audit';
import { eligibleSubordinateIds, roleHierarchyHasCycle } from '@/domain/supervision-hierarchy';
import type { PermissionKey } from '@/domain/permissions';
import type {
  Brand,
  BrandBranchLink,
  Branch,
  CategoryKind,
  Company,
  Membership,
  MembershipPlacement,
  MembershipRole,
  Role,
  RolePermission,
  RoleScope,
  ScopeType,
  SupervisionRelation,
  TaskCategory,
} from '@/domain/types';

export interface Actor {
  userId: string;
  membershipId: string | null;
  companyId: string;
  isPlatformSupport?: boolean;
}

async function actorIsCompanyOwner(gateway: DataGateway, actor: Actor): Promise<boolean> {
  if (!actor.membershipId) return false;
  const membership = await gateway.table('memberships').get(actor.membershipId).catch(() => null);
  return Boolean(membership?.companyId === actor.companyId && membership.isCompanyOwner);
}

export async function listCompanies(gateway: DataGateway): Promise<Company[]> {
  return gateway.table('companies').list({ orderBy: [{ field: 'name', direction: 'asc' }] });
}

export async function getCompany(gateway: DataGateway, companyId: string): Promise<Company | null> {
  return gateway.table('companies').get(companyId);
}

export async function updateCompany(
  gateway: DataGateway,
  _actor: Actor,
  companyId: string,
  patch: Partial<Company>,
): Promise<Company> {
  if (patch.name !== undefined && patch.name.trim().length < 2) {
    throw AppError.validation('اسم المنشأة قصير جدًا');
  }
  const updated = await gateway.table('companies').update(companyId, patch);
  await gateway.recordAudit({
    companyId,
    action: AUDIT_ACTIONS.companyUpdated,
    entityType: 'company',
    entityId: companyId,
    after: patch,
  });
  return updated;
}

/* ------------------------------- العلامات -------------------------------- */

export async function listBrands(gateway: DataGateway, companyId: string): Promise<Brand[]> {
  return gateway.table('brands').list({
    filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } },
    orderBy: [{ field: 'sortOrder', direction: 'asc' }],
  });
}

export async function createBrand(
  gateway: DataGateway,
  actor: Actor,
  input: { name: string; code: string; description?: string | null; phone?: string | null; logoAttachmentId?: string | null },
): Promise<Brand> {
  const name = input.name.trim();
  const code = input.code.trim().toUpperCase();
  if (name.length < 2) throw AppError.validation('اسم العلامة قصير جدًا');
  if (!/^[A-Z0-9_-]{2,24}$/.test(code)) {
    throw AppError.validation('رمز العلامة يجب أن يكون بالإنجليزية (2-24 حرفًا)');
  }
  const existing = await gateway.table('brands').list({
    filter: { companyId: { op: 'eq', value: actor.companyId }, code: { op: 'eq', value: code } },
  });
  if (existing.length > 0) throw AppError.conflict('رمز العلامة مستخدم مسبقًا');

  const now = new Date().toISOString();
  const brand = await gateway.table('brands').insert({
    id: newId(),
    companyId: actor.companyId,
    name,
    code,
    description: input.description?.trim() || null,
    phone: input.phone?.trim() || null,
    logoAttachmentId: input.logoAttachmentId ?? null,
    status: 'active',
    settings: {},
    sortOrder: existing.length,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
  });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.brandCreated,
    entityType: 'brand',
    entityId: brand.id,
    after: { name, code },
  });
  return brand;
}

export async function updateBrand(
  gateway: DataGateway,
  actor: Actor,
  brandId: string,
  patch: Partial<Brand>,
): Promise<Brand> {
  const brand = await gateway.table('brands').get(brandId);
  if (!brand) throw AppError.notFound('العلامة غير موجودة');
  const updated = await gateway.table('brands').update(brandId, { ...patch, updatedAt: new Date().toISOString() });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.brandUpdated,
    entityType: 'brand',
    entityId: brandId,
    before: { name: brand.name, code: brand.code, status: brand.status },
    after: patch,
  });
  return updated;
}

/* -------------------------------- الفروع --------------------------------- */

export async function listBranches(gateway: DataGateway, companyId: string): Promise<Branch[]> {
  return gateway.table('branches').list({
    filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } },
    orderBy: [{ field: 'name', direction: 'asc' }],
  });
}

export async function createBranch(
  gateway: DataGateway,
  actor: Actor,
  input: {
    name: string;
    code: string;
    address?: string | null;
    phone?: string | null;
    brandId?: string | null;
    timeZone?: string | null;
    logoAttachmentId?: string | null;
    inheritBrandLogo?: boolean;
  },
): Promise<Branch> {
  const name = input.name.trim();
  const code = input.code.trim().toUpperCase();
  if (name.length < 2) throw AppError.validation('اسم الفرع قصير جدًا');
  if (!/^[A-Z0-9_-]{2,24}$/.test(code)) {
    throw AppError.validation('رمز الفرع يجب أن يكون بالإنجليزية (2-24 حرفًا)');
  }
  const existing = await gateway.table('branches').list({
    filter: { companyId: { op: 'eq', value: actor.companyId }, code: { op: 'eq', value: code } },
  });
  if (existing.length > 0) throw AppError.conflict('رمز الفرع مستخدم مسبقًا');
  if (input.brandId) {
    const brand = await gateway.table('brands').get(input.brandId);
    if (!brand || brand.companyId !== actor.companyId) throw AppError.validation('العلامة غير صالحة');
  }
  if (input.inheritBrandLogo && !input.brandId) {
    throw AppError.validation('اختر علامة تجارية قبل وراثة صورتها');
  }

  const now = new Date().toISOString();
  const branch = await gateway.table('branches').insert({
    id: newId(),
    companyId: actor.companyId,
    brandId: input.brandId ?? null,
    name,
    code,
    address: input.address?.trim() || null,
    phone: input.phone?.trim() || null,
    timeZone: (input.timeZone as Branch['timeZone']) ?? null,
    logoAttachmentId: input.logoAttachmentId ?? null,
    inheritBrandLogo: input.inheritBrandLogo ?? false,
    status: 'active',
    settings: {},
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
  });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.branchCreated,
    entityType: 'branch',
    entityId: branch.id,
    after: { name, code },
  });
  return branch;
}

export async function updateBranch(
  gateway: DataGateway,
  actor: Actor,
  branchId: string,
  patch: Partial<Branch>,
): Promise<Branch> {
  const branch = await gateway.table('branches').get(branchId);
  if (!branch) throw AppError.notFound('الفرع غير موجود');
  const nextBrandId = patch.brandId === undefined ? branch.brandId : patch.brandId;
  if (patch.brandId) {
    const brand = await gateway.table('brands').get(patch.brandId);
    if (!brand || brand.companyId !== actor.companyId) throw AppError.validation('العلامة غير صالحة');
  }
  if ((patch.inheritBrandLogo ?? branch.inheritBrandLogo) && !nextBrandId) {
    throw AppError.validation('اختر علامة تجارية قبل وراثة صورتها');
  }
  const updated = await gateway.table('branches').update(branchId, {
    ...patch,
    updatedAt: new Date().toISOString(),
  });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.branchUpdated,
    entityType: 'branch',
    entityId: branchId,
    before: { name: branch.name, code: branch.code, status: branch.status },
    after: patch,
  });
  return updated;
}

/* --------------------------- ربط العلامات بالفروع -------------------------- */

export async function listBrandBranchLinks(
  gateway: DataGateway,
  companyId: string,
): Promise<BrandBranchLink[]> {
  return gateway.table('brand_branch_links').list({
    filter: { companyId: { op: 'eq', value: companyId } },
    orderBy: [{ field: 'createdAt', direction: 'asc' }],
  });
}

/**
 * استبدال مجموعة الفروع المرتبطة بعلامة (متعدد-إلى-متعدد).
 * يحافظ على الروابط القائمة، ويضيف/يحذف الفرق فقط، ويسجّل كل تغيير في التدقيق.
 */
export async function setBrandBranches(
  gateway: DataGateway,
  actor: Actor,
  brandId: string,
  branchIds: string[],
): Promise<void> {
  const brand = await gateway.table('brands').get(brandId);
  if (!brand || brand.companyId !== actor.companyId) throw AppError.notFound('العلامة غير موجودة');

  const branches = await listBranches(gateway, actor.companyId);
  const validBranches = new Set(branches.map((branch) => branch.id));
  const target = new Set(branchIds);
  for (const branchId of target) {
    if (!validBranches.has(branchId)) throw AppError.validation('فرع غير صالح لهذه المنشأة');
  }

  const existing = await gateway.table('brand_branch_links').list({
    filter: {
      companyId: { op: 'eq', value: actor.companyId },
      brandId: { op: 'eq', value: brandId },
    },
  });
  const existingByBranch = new Map(existing.map((link) => [link.branchId, link]));

  for (const link of existing) {
    if (target.has(link.branchId)) continue;
    await gateway.table('brand_branch_links').remove(link.id);
    await gateway.recordAudit({
      companyId: actor.companyId,
      action: AUDIT_ACTIONS.branchUnlinkedFromBrand,
      entityType: 'brand_branch_link',
      entityId: link.id,
      before: { brandId, branchId: link.branchId },
    });
  }

  const now = new Date().toISOString();
  for (const branchId of target) {
    if (existingByBranch.has(branchId)) continue;
    const link = await gateway.table('brand_branch_links').insert({
      id: newId(),
      companyId: actor.companyId,
      brandId,
      branchId,
      isPrimary: false,
      createdAt: now,
    });
    await gateway.recordAudit({
      companyId: actor.companyId,
      action: AUDIT_ACTIONS.branchLinkedToBrand,
      entityType: 'brand_branch_link',
      entityId: link.id,
      after: { brandId, branchId },
    });
  }
}

/* -------------------------------- الفئات --------------------------------- */

export async function listCategories(gateway: DataGateway, companyId: string): Promise<TaskCategory[]> {
  return gateway.table('task_categories').list({
    filter: { companyId: { op: 'eq', value: companyId } },
    orderBy: [{ field: 'sortOrder', direction: 'asc' }],
  });
}

export async function createCategory(
  gateway: DataGateway,
  actor: Actor,
  input: { name: string; kind: CategoryKind; color?: string },
): Promise<TaskCategory> {
  const name = input.name.trim();
  if (name.length < 2) throw AppError.validation('اسم الفئة قصير جدًا');
  const existing = await gateway.table('task_categories').list({
    filter: { companyId: { op: 'eq', value: actor.companyId } },
  });
  const now = new Date().toISOString();
  const category = await gateway.table('task_categories').insert({
    id: newId(),
    companyId: actor.companyId,
    key: `custom_${Date.now().toString(36)}`,
    name,
    description: null,
    kind: input.kind,
    color: input.color ?? '#6b7280',
    icon: null,
    isSystem: false,
    sortOrder: existing.length,
    status: 'active',
    createdAt: now,
    updatedAt: now,
  });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.taskCategoryCreated,
    entityType: 'task_category',
    entityId: category.id,
    after: { name, kind: input.kind },
  });
  return category;
}

export async function updateCategory(
  gateway: DataGateway,
  actor: Actor,
  categoryId: string,
  patch: Partial<Pick<TaskCategory, 'name' | 'description' | 'kind' | 'color' | 'status' | 'sortOrder'>>,
): Promise<TaskCategory> {
  const category = await gateway.table('task_categories').get(categoryId);
  if (!category || category.companyId !== actor.companyId) throw AppError.notFound('الفئة غير موجودة');
  if (patch.name !== undefined && patch.name.trim().length < 2) {
    throw AppError.validation('اسم الفئة قصير جدًا');
  }
  const updated = await gateway.table('task_categories').update(categoryId, {
    ...patch,
    updatedAt: new Date().toISOString(),
  });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.taskCategoryUpdated,
    entityType: 'task_category',
    entityId: categoryId,
    before: { name: category.name, kind: category.kind, status: category.status },
    after: patch,
  });
  return updated;
}

/* ------------------------------ العضويات -------------------------------- */

export async function listMemberships(gateway: DataGateway, companyId: string): Promise<Membership[]> {
  return gateway.table('memberships').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'asc' }],
  });
}

export interface MembershipDirectoryEntry {
  membership: Membership;
  profile: { id: string; email: string; fullName: string; phone: string | null; avatarDataUrl: string | null } | null;
  roleIds: string[];
  placements: MembershipPlacement[];
}

/** دليل العضويات مع الملفات الشخصية والأدوار — لاستخدامه في القوائم والإسناد. */
export async function listMembershipDirectory(
  gateway: DataGateway,
  companyId: string,
): Promise<MembershipDirectoryEntry[]> {
  const memberships = await listMemberships(gateway, companyId);
  if (memberships.length === 0) return [];
  const [profiles, emails] = await Promise.all([
    gateway.table('membership_profiles').list({ filter: { membershipId: { op: 'in', value: memberships.map((m) => m.id) } } }),
    gateway.commands.listCompanyMemberEmails({ companyId }),
  ]);
  const profileMap = new Map(profiles.map((profile) => [profile.membershipId, profile]));
  const emailMap = new Map(emails.map((entry) => [entry.membershipId, entry.email]));
  const membershipIds = memberships.map((membership) => membership.id);
  const membershipRoles = await gateway.table('membership_roles').list({
    filter: { membershipId: { op: 'in', value: membershipIds } },
  });
  const placements = await gateway.table('membership_placements').list({
    filter: {
      membershipId: { op: 'in', value: membershipIds },
      deletedAt: { op: 'isNull' },
    },
  });
  return memberships.map((membership) => ({
    membership,
    profile: (() => { const profile = profileMap.get(membership.id); return profile ? { id: profile.userId, email: emailMap.get(membership.id) ?? '', fullName: profile.fullName, phone: profile.phone, avatarDataUrl: profile.avatarDataUrl } : null; })(),
    roleIds: membershipRoles
      .filter((entry) => entry.membershipId === membership.id)
      .map((entry) => entry.roleId),
    placements: placements.filter((entry) => entry.membershipId === membership.id),
  }));
}

export async function addMembershipByEmail(
  gateway: DataGateway,
  actor: Actor,
  input: {
    email: string;
    fullName?: string | null;
    jobTitle?: string | null;
    branchId?: string | null;
    roleIds: string[];
    /** عند تمريره: يُنشأ حساب دخول جديد بهذا البريد وكلمة المرور قبل ربط العضوية. */
    password?: string | null;
  },
): Promise<Membership> {
  const membership = await gateway.commands.addMembership({
    companyId: actor.companyId,
    email: input.email,
    fullName: input.fullName ?? null,
    jobTitle: input.jobTitle ?? null,
    primaryBranchId: input.branchId ?? null,
    roleIds: input.roleIds,
    actorMembershipId: actor.membershipId,
    actorUserId: actor.userId,
    password: input.password ?? null,
  });
  if (membership.primaryBranchId) {
    const existing = await gateway.table('membership_placements').list({
      filter: { membershipId: { op: 'eq', value: membership.id } },
    });
    if (!existing.some((placement) => placement.scopeType === 'branch' && placement.scopeId === membership.primaryBranchId)) {
      await gateway.table('membership_placements').insert({
        id: newId(),
        companyId: actor.companyId,
        membershipId: membership.id,
        scopeType: 'branch',
        scopeId: membership.primaryBranchId,
        isPrimary: true,
        createdAt: new Date().toISOString(),
        deletedAt: null,
        deletedBy: null,
        deletedReason: null,
      });
    }
  }
  return membership;
}

export async function updateMembership(
  gateway: DataGateway,
  actor: Actor,
  membershipId: string,
  patch: Partial<Membership>,
): Promise<Membership> {
  const membership = await gateway.table('memberships').get(membershipId);
  if (!membership || membership.companyId !== actor.companyId) throw AppError.notFound('العضوية غير موجودة');
  if (patch.isCompanyOwner === true && !membership.isCompanyOwner) {
    // تعيين مالك شركة جديد إجراء حساس: يتطلب أن يكون المنفذ مالكًا حاليًا.
    const actorMembership = actor.membershipId ? await gateway.table('memberships').get(actor.membershipId) : null;
    if (!actor.isPlatformSupport && !actorMembership?.isCompanyOwner) {
      throw AppError.forbidden('تعيين مسؤول المنشأة متاح لمسؤول المنشأة الحالي فقط');
    }
  }
  const updated = await gateway.table('memberships').update(membershipId, {
    ...patch,
    updatedAt: new Date().toISOString(),
  });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action:
      patch.status !== undefined && patch.status !== membership.status
        ? AUDIT_ACTIONS.membershipStatusChanged
        : AUDIT_ACTIONS.membershipUpdated,
    entityType: 'membership',
    entityId: membershipId,
    before: { status: membership.status, jobTitle: membership.jobTitle },
    after: patch,
  });
  return updated;
}

export async function updateMemberProfile(
  gateway: DataGateway,
  actor: Actor,
  membershipId: string,
  input: { fullName: string; jobTitle: string },
): Promise<void> {
  const fullName = input.fullName.trim();
  const jobTitle = input.jobTitle.trim();
  if (fullName.length < 2 || fullName.length > 120) {
    throw AppError.validation('الاسم يجب أن يكون بين حرفين و120 حرفًا');
  }
  if (jobTitle.length > 120) throw AppError.validation('المسمى الوظيفي أطول من الحد المسموح');
  await gateway.commands.updateMembershipProfile({
    companyId: actor.companyId,
    membershipId,
    fullName,
    jobTitle: jobTitle || null,
  });
}

export async function updateMemberAccountAuth(
  gateway: DataGateway,
  actor: Actor,
  membershipId: string,
  input: { email?: string; password?: string },
): Promise<{ email: string; emailChanged: boolean; passwordChanged: boolean; auditWarning?: string }> {
  const command = gateway.commands.updateMemberAccountAuth;
  if (gateway.kind !== 'supabase' || !command) {
    throw AppError.validation('تعديل بيانات الدخول متاح عند الاتصال الآمن بالمنصة فقط.');
  }
  const email = input.email?.trim().toLowerCase() ?? '';
  const password = input.password ?? '';
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw AppError.validation('أدخل بريدًا إلكترونيًا صالحًا.');
  }
  if (password && (password.length < 10 || password.length > 72)) {
    throw AppError.validation('كلمة المرور الجديدة يجب أن تكون بين 10 و72 حرفًا.');
  }
  if (!email && !password) throw AppError.validation('أدخل بريدًا جديدًا أو كلمة مرور جديدة.');
  return command({
    companyId: actor.companyId,
    membershipId,
    ...(email ? { email } : {}),
    ...(password ? { password } : {}),
  });
}

export async function listMembershipRoles(
  gateway: DataGateway,
  membershipIds: string[],
): Promise<MembershipRole[]> {
  if (membershipIds.length === 0) return [];
  return gateway.table('membership_roles').list({
    filter: { membershipId: { op: 'in', value: membershipIds } },
  });
}

export async function setMembershipRoles(
  gateway: DataGateway,
  actor: Actor,
  membershipId: string,
  roleIds: string[],
): Promise<void> {
  const membership = await gateway.table('memberships').get(membershipId);
  if (!membership || membership.companyId !== actor.companyId) throw AppError.notFound('العضوية غير موجودة');

  const roles = await gateway.table('roles').list({
    filter: { companyId: { op: 'eq', value: actor.companyId } },
  });
  const validRoles = new Set(roles.filter((role) => !role.deletedAt && !role.isPlatformRole).map((role) => role.id));
  for (const roleId of roleIds) {
    if (!validRoles.has(roleId)) throw AppError.validation('دور غير صالح لهذه المنشأة');
  }

  const existing = await gateway.table('membership_roles').list({
    filter: { membershipId: { op: 'eq', value: membershipId } },
  });
  const existingRoleIds = new Set(existing.map((row) => row.roleId));
  const target = new Set(roleIds);

  for (const row of existing) {
    if (!target.has(row.roleId)) {
      await gateway.table('membership_roles').remove(row.id);
    }
  }
  const now = new Date().toISOString();
  for (const roleId of target) {
    if (existingRoleIds.has(roleId)) continue;
    await gateway.table('membership_roles').insert({
      id: newId(),
      membershipId,
      roleId,
      assignedBy: actor.userId,
      assignedAt: now,
      expiresAt: null,
    });
  }
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.membershipRolesChanged,
    entityType: 'membership',
    entityId: membershipId,
    before: { roleIds: [...existingRoleIds] },
    after: { roleIds: [...target] },
  });
}

/* --------------------------- دورة حياة التنظيم ---------------------------- */

/** مسار موحّد لكل إجراءات الحذف/الاستعادة/التنشيط/الإيقاف/الحذف النهائي. */
export async function manageOrgEntity(
  gateway: DataGateway,
  input: OrgLifecycleInput,
): Promise<void> {
  const reason = input.reason?.trim() ?? '';
  if (reason.length > 500) throw AppError.validation('سبب الحذف أطول من الحد المسموح');
  if (input.action === 'purge' && !input.confirmation?.trim()) {
    throw AppError.validation('الحذف النهائي يتطلب كتابة اسم العنصر للتأكيد');
  }
  await gateway.commands.manageOrgEntity({
    ...input,
    reason: reason || null,
    confirmation: input.confirmation?.trim() || null,
  });
}

export async function listOrgArchive(
  gateway: DataGateway,
  query: OrgArchiveQuery,
): Promise<ArchivedOrgRecord[]> {
  return gateway.commands.listOrgArchive(query);
}

/** إضافة ارتباط موظف بفرع أو علامة دون المساس بالعضوية أو بقية الارتباطات. */
export async function addMembershipPlacement(
  gateway: DataGateway,
  actor: Actor,
  input: {
    membershipId: string;
    scopeType: 'branch' | 'brand';
    scopeId: string;
    isPrimary?: boolean;
  },
): Promise<MembershipPlacement> {
  const membership = await gateway.table('memberships').get(input.membershipId);
  if (!membership || membership.companyId !== actor.companyId) {
    throw AppError.notFound('الموظف غير موجود');
  }
  const scope =
    input.scopeType === 'branch'
      ? await gateway.table('branches').get(input.scopeId)
      : await gateway.table('brands').get(input.scopeId);
  if (!scope || scope.companyId !== actor.companyId || scope.deletedAt || scope.status !== 'active') {
    throw AppError.validation(
      input.scopeType === 'branch' ? 'الفرع غير متاح' : 'العلامة غير متاحة',
    );
  }
  const existing = await gateway.table('membership_placements').list({
    filter: {
      membershipId: { op: 'eq', value: input.membershipId },
      scopeType: { op: 'eq', value: input.scopeType },
      scopeId: { op: 'eq', value: input.scopeId },
    },
  });
  if (existing.length > 0) throw AppError.conflict('هذا الارتباط موجود مسبقًا');

  const now = new Date().toISOString();
  const placement = await gateway.table('membership_placements').insert({
    id: newId(),
    companyId: actor.companyId,
    membershipId: input.membershipId,
    scopeType: input.scopeType,
    scopeId: input.scopeId,
    isPrimary: input.isPrimary ?? false,
    createdAt: now,
    deletedAt: null,
    deletedBy: null,
    deletedReason: null,
  });
  if (
    input.scopeType === 'branch' &&
    (input.isPrimary || !membership.primaryBranchId)
  ) {
    await gateway.table('memberships').update(input.membershipId, {
      primaryBranchId: input.scopeId,
      updatedAt: now,
    });
  }
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.membershipLinkAdded,
    entityType: 'membership_placement',
    entityId: placement.id,
    after: {
      membershipId: input.membershipId,
      scopeType: input.scopeType,
      scopeId: input.scopeId,
    },
  });
  return placement;
}

/* --------------------------------- الأدوار -------------------------------- */

export async function listRoles(gateway: DataGateway, companyId: string): Promise<Role[]> {
  return gateway.table('roles').list({
    filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } },
    orderBy: [{ field: 'priority', direction: 'asc' }],
  });
}

export async function listDeletedRoles(gateway: DataGateway, actor: Actor): Promise<Role[]> {
  return gateway.commands.listDeletedCompanyRoles({ companyId: actor.companyId });
}

export async function listRolePermissions(gateway: DataGateway, roleIds: string[]): Promise<RolePermission[]> {
  if (roleIds.length === 0) return [];
  return gateway.table('role_permissions').list({ filter: { roleId: { op: 'in', value: roleIds } } });
}

export async function listRoleScopes(gateway: DataGateway, roleIds: string[]): Promise<RoleScope[]> {
  if (roleIds.length === 0) return [];
  return gateway.table('role_scopes').list({ filter: { roleId: { op: 'in', value: roleIds } } });
}

export async function createRole(
  gateway: DataGateway,
  actor: Actor,
  input: {
    name: string;
    description?: string | null;
    priority: number;
    supervisesRoleIds: string[];
    permissions: PermissionKey[];
    scopes: Array<{ scopeType: ScopeType; targetId: string | null }>;
  },
): Promise<Role> {
  const name = input.name.trim();
  if (name.length < 2) throw AppError.validation('اسم الدور قصير جدًا');
  if (input.permissions.includes('role.manage') && !(await actorIsCompanyOwner(gateway, actor))) {
    throw AppError.forbidden('منح صلاحية إدارة الأدوار متاح لمالك المنشأة فقط');
  }
  const supervisesRoleIds = [...new Set(input.supervisesRoleIds)];
  const existingRoles = await listRoles(gateway, actor.companyId);
  const existingRoleIds = new Set(existingRoles.map((entry) => entry.id));
  if (supervisesRoleIds.some((id) => !existingRoleIds.has(id))) {
    throw AppError.validation('تتضمن قائمة التسلسل دورًا غير صالح لهذه المنشأة');
  }
  const now = new Date().toISOString();
  const role = await gateway.table('roles').insert({
    id: newId(),
    companyId: actor.companyId,
    key: `custom_${Date.now().toString(36)}`,
    name,
    description: input.description?.trim() || null,
    priority: input.priority,
    supervisesRoleIds,
    isSystem: false,
    isPlatformRole: false,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
  });

  try {
    for (const permission of new Set(input.permissions)) {
      await gateway.table('role_permissions').insert({
        id: newId(),
        roleId: role.id,
        permissionKey: permission,
      });
    }
    for (const scope of input.scopes) {
      await gateway.table('role_scopes').insert({
        id: newId(),
        roleId: role.id,
        scopeType: scope.scopeType,
        targetId: scope.targetId,
      });
    }
  } catch (error) {
    // إخفاء الدور الجزئي عبر المسار الذري؛ التحديث المباشر لـ deletedAt محظور.
    await gateway.commands.deleteCompanyRole({
      companyId: actor.companyId,
      roleId: role.id,
      expectedMemberIds: [],
      expectedDependentRoleIds: [],
    }).catch(() => undefined);
    throw error;
  }

  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.roleCreated,
    entityType: 'role',
    entityId: role.id,
    after: { name, permissionCount: input.permissions.length },
  });
  return role;
}

export async function updateRole(
  gateway: DataGateway,
  actor: Actor,
  roleId: string,
  patch: Partial<Role>,
): Promise<Role> {
  const role = await gateway.table('roles').get(roleId);
  if (!role || role.companyId !== actor.companyId) throw AppError.notFound('الدور غير موجود');
  if (patch.supervisesRoleIds !== undefined) {
    const supervisesRoleIds = [...new Set(patch.supervisesRoleIds)];
    if (supervisesRoleIds.includes(roleId)) throw AppError.validation('لا يمكن للدور الإشراف على نفسه');
    const existingRoles = await listRoles(gateway, actor.companyId);
    const existingRoleIds = new Set(existingRoles.map((entry) => entry.id));
    if (supervisesRoleIds.some((id) => !existingRoleIds.has(id))) {
      throw AppError.validation('تتضمن قائمة التسلسل دورًا غير صالح لهذه المنشأة');
    }
    const proposedRules = new Map(existingRoles.map((entry) => [
      entry.id,
      entry.id === roleId ? supervisesRoleIds : entry.supervisesRoleIds ?? [],
    ]));
    if (roleHierarchyHasCycle(existingRoles, proposedRules)) {
      throw AppError.validation('لا يمكن حفظ التسلسل لأنه ينشئ حلقة إشراف بين الأدوار');
    }
    patch = { ...patch, supervisesRoleIds };
  }
  const updated = await gateway.table('roles').update(roleId, {
    ...patch,
    updatedAt: new Date().toISOString(),
  });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.roleUpdated,
    entityType: 'role',
    entityId: roleId,
    before: { name: role.name, priority: role.priority },
    after: patch,
  });
  return updated;
}

export async function getRoleDeletionImpact(
  gateway: DataGateway,
  actor: Actor,
  roleId: string,
): Promise<RoleDeletionImpact> {
  const role = await gateway.table('roles').get(roleId);
  if (!role || role.companyId !== actor.companyId || role.deletedAt) {
    throw AppError.notFound('الدور غير موجود');
  }
  return gateway.commands.getRoleDeletionImpact({ companyId: actor.companyId, roleId });
}

export async function deleteRole(
  gateway: DataGateway,
  actor: Actor,
  roleId: string,
  confirmedImpact: RoleDeletionImpact,
): Promise<void> {
  const role = await gateway.table('roles').get(roleId);
  if (!role || role.companyId !== actor.companyId || role.deletedAt) {
    throw AppError.notFound('الدور غير موجود');
  }
  if (confirmedImpact.roleId !== roleId || confirmedImpact.roleName !== role.name) {
    throw AppError.conflict('تغيّرت بيانات الدور؛ أغلق التنبيه وافتح الحذف مجددًا لمراجعة الأثر قبل الموافقة.');
  }
  await gateway.commands.deleteCompanyRole({
    companyId: actor.companyId,
    roleId,
    expectedMemberIds: [...new Set(confirmedImpact.assignedMembers.map((member) => member.membershipId))].sort(),
    expectedDependentRoleIds: [...new Set(confirmedImpact.dependentRoles.map((dependent) => dependent.id))].sort(),
  });
}

export async function restoreRole(gateway: DataGateway, actor: Actor, roleId: string): Promise<Role> {
  return gateway.commands.restoreCompanyRole({ companyId: actor.companyId, roleId });
}

export async function setRolePermissions(
  gateway: DataGateway,
  actor: Actor,
  roleId: string,
  permissions: PermissionKey[],
): Promise<void> {
  const role = await gateway.table('roles').get(roleId);
  if (!role || role.companyId !== actor.companyId) throw AppError.notFound('الدور غير موجود');
  const existing = await gateway.table('role_permissions').list({
    filter: { roleId: { op: 'eq', value: roleId } },
  });
  const existingKeys = new Set(existing.map((row) => row.permissionKey));
  const target = new Set(permissions);
  if (
    existingKeys.has('role.manage') !== target.has('role.manage') &&
    !(await actorIsCompanyOwner(gateway, actor))
  ) {
    throw AppError.forbidden('منح أو سحب صلاحية إدارة الأدوار متاح لمالك المنشأة فقط');
  }

  for (const row of existing) {
    if (!target.has(row.permissionKey)) await gateway.table('role_permissions').remove(row.id);
  }
  for (const permission of target) {
    if (existingKeys.has(permission)) continue;
    await gateway.table('role_permissions').insert({ id: newId(), roleId, permissionKey: permission });
  }
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.rolePermissionsChanged,
    entityType: 'role',
    entityId: roleId,
    before: { permissions: [...existingKeys] },
    after: { permissions: [...target] },
  });
}

export async function setRoleScopes(
  gateway: DataGateway,
  actor: Actor,
  roleId: string,
  scopes: Array<{ scopeType: ScopeType; targetId: string | null }>,
): Promise<void> {
  const role = await gateway.table('roles').get(roleId);
  if (!role || role.companyId !== actor.companyId) throw AppError.notFound('الدور غير موجود');
  const existing = await gateway.table('role_scopes').list({ filter: { roleId: { op: 'eq', value: roleId } } });
  for (const row of existing) await gateway.table('role_scopes').remove(row.id);
  for (const scope of scopes) {
    await gateway.table('role_scopes').insert({
      id: newId(),
      roleId,
      scopeType: scope.scopeType,
      targetId: scope.targetId,
    });
  }
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.roleScopesChanged,
    entityType: 'role',
    entityId: roleId,
    after: { scopes },
  });
}

/* ------------------------------- الإشراف -------------------------------- */

export async function listSupervision(gateway: DataGateway, companyId: string): Promise<SupervisionRelation[]> {
  return gateway.table('supervision_relations').list({
    filter: { companyId: { op: 'eq', value: companyId } },
    orderBy: [{ field: 'createdAt', direction: 'desc' }],
  });
}

export async function createSupervision(
  gateway: DataGateway,
  actor: Actor,
  input: {
    supervisorMembershipIds: string[];
    subordinateMembershipIds: string[];
    canViewTasks?: boolean;
    canAssignTasks?: boolean;
    canApproveTasks?: boolean;
    canViewReports?: boolean;
    note?: string | null;
  },
): Promise<SupervisionRelation[]> {
  const supervisors = [...new Set(input.supervisorMembershipIds)];
  const subordinates = [...new Set(input.subordinateMembershipIds)];
  if (!supervisors.length || !subordinates.length) throw AppError.validation('اختر مشرفًا ومرؤوسًا واحدًا على الأقل');
  if (supervisors.some((id) => subordinates.includes(id))) {
    throw AppError.validation('لا يمكن أن يكون المستخدم مشرفًا على نفسه');
  }

  const memberIds = [...new Set([...supervisors, ...subordinates])];
  const members = await Promise.all(memberIds.map((id) => gateway.table('memberships').get(id)));
  const validIds = new Set(members.filter((member) => member?.companyId === actor.companyId).map((member) => member!.id));
  if (supervisors.some((id) => !validIds.has(id))) throw AppError.validation('أحد المشرفين غير صالح');
  if (subordinates.some((id) => !validIds.has(id))) throw AppError.validation('أحد المرؤوسين غير صالح');

  const [membershipRoles, companyRoles] = await Promise.all([
    gateway.table('membership_roles').list({ filter: { membershipId: { op: 'in', value: memberIds } } }),
    listRoles(gateway, actor.companyId),
  ]);
  const nowMs = Date.now();
  const roleIdsByMembership = new Map<string, string[]>();
  for (const membershipRole of membershipRoles) {
    if (membershipRole.expiresAt && Date.parse(membershipRole.expiresAt) <= nowMs) continue;
    const assigned = roleIdsByMembership.get(membershipRole.membershipId) ?? [];
    assigned.push(membershipRole.roleId);
    roleIdsByMembership.set(membershipRole.membershipId, assigned);
  }
  const eligibleIds = eligibleSubordinateIds(
    members.filter((member): member is Membership => Boolean(member && member.companyId === actor.companyId)),
    roleIdsByMembership,
    companyRoles,
    supervisors,
  );
  if (subordinates.some((id) => !eligibleIds.has(id))) {
    throw AppError.validation('أحد المرؤوسين المختارين لا يقع ضمن التسلسل الإداري المسموح للمشرفين');
  }

  const pairs = supervisors.flatMap((supervisorMembershipId) =>
    subordinates.map((subordinateMembershipId) => ({ supervisorMembershipId, subordinateMembershipId })),
  );
  for (const pair of pairs) {
    const existing = await gateway.table('supervision_relations').list({
      filter: {
        companyId: { op: 'eq', value: actor.companyId },
        supervisorMembershipId: { op: 'eq', value: pair.supervisorMembershipId },
        subordinateMembershipId: { op: 'eq', value: pair.subordinateMembershipId },
        endsAt: { op: 'isNull' },
      },
    });
    if (existing.length) throw AppError.conflict('تتضمن الاختيارات علاقة إشراف نشطة موجودة مسبقًا');
  }

  const created: SupervisionRelation[] = [];
  for (const pair of pairs) {
    const now = new Date().toISOString();
    const relation = await gateway.table('supervision_relations').insert({
      id: newId(), companyId: actor.companyId, ...pair,
      canViewTasks: input.canViewTasks ?? true,
      canAssignTasks: input.canAssignTasks ?? false,
      canApproveTasks: input.canApproveTasks ?? false,
      canViewReports: input.canViewReports ?? false,
      startsAt: now, endsAt: null, createdBy: actor.userId, createdAt: now,
      note: input.note?.trim() || null,
    });
    created.push(relation);
    await gateway.recordAudit({
      companyId: actor.companyId, action: AUDIT_ACTIONS.supervisionCreated,
      entityType: 'supervision_relation', entityId: relation.id, after: pair,
    });
  }
  return created;
}

export async function endSupervision(
  gateway: DataGateway,
  actor: Actor,
  relationId: string,
): Promise<void> {
  const relation = await gateway.table('supervision_relations').get(relationId);
  if (!relation || relation.companyId !== actor.companyId) throw AppError.notFound('علاقة الإشراف غير موجودة');
  await gateway.table('supervision_relations').update(relationId, { endsAt: new Date().toISOString() });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.supervisionRevoked,
    entityType: 'supervision_relation',
    entityId: relationId,
  });
}
